export const DIALOG_SIZE_KEY = 'bytax-feedback:dialog-size';
export const DIALOG_MIN = { width: 320, height: 240 } as const;
export const VIEWPORT_MARGIN = 16;

export interface DialogSize {
  width: number;
  height: number;
}

/**
 * Clamps to the 320×240 floor and the viewport−32px cap. When the two
 * conflict (viewport smaller than the floor), the viewport cap wins —
 * the dialog must fit the screen.
 */
export function clampSize(
  size: DialogSize,
  viewport: { width: number; height: number },
): DialogSize {
  const maxWidth = viewport.width - VIEWPORT_MARGIN * 2;
  const maxHeight = viewport.height - VIEWPORT_MARGIN * 2;
  return {
    width: Math.min(Math.max(size.width, DIALOG_MIN.width), maxWidth),
    height: Math.min(Math.max(size.height, DIALOG_MIN.height), maxHeight),
  };
}

/**
 * Best-effort persistence: private mode, quota errors and garbage payloads
 * all degrade to null (caller falls back to default sizing). `storage` is
 * nullable because accessing the `localStorage` global itself can throw
 * (cookies-blocked browsers) before a `Storage` instance is ever obtained —
 * callers pass `null` in that case rather than skip the call entirely.
 */
export function loadDialogSize(
  storage: Storage | null,
  viewport: { width: number; height: number },
): DialogSize | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(DIALOG_SIZE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { width, height } = parsed as Record<string, unknown>;
    if (typeof width !== 'number' || typeof height !== 'number') return null;
    if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
    return clampSize({ width, height }, viewport);
  } catch {
    return null;
  }
}

export function saveDialogSize(storage: Storage | null, size: DialogSize): void {
  if (!storage) return;
  try {
    storage.setItem(DIALOG_SIZE_KEY, JSON.stringify(size));
  } catch {
    // best-effort
  }
}
